import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { getFillPriceFromStore, getNotationFromStore } from "./fillPriceHelpers";
import {
  returnDecimalFormatting,
  formatValueWithNotation,
  isCurrencyNotation,
  isPriceNotation,
} from "@/lib/globalFormatterHelper";
import {
  containsHyphenInQuote,
  formatDollarFractionString,
} from "@/lib/businessRules/dollarFractionBusinessRule";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";

/**
 * Format value only (for copy/paste - NO notation symbols)
 * Applies notation-based rounding but does not include notation suffix.
 * For $fr: returns the fraction string as-is (e.g., "99-16").
 *
 * @param price - The fill price value (string from API)
 * @param notation - The notation type for determining decimal places
 * @returns Formatted string with value only (no notation suffix)
 */
export function formatFillPriceValue(
  price: string | undefined,
  notation?: NotationToDecimalValueType,
): string {
  if (price === null || price === undefined || price === "") {
    return "";
  }

  // $fr: if the price is a fraction string, return as-is with padding
  if (notation === "$fr" && containsHyphenInQuote(price)) {
    return formatDollarFractionString(price);
  }

  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    return "";
  }

  // Apply notation-based rounding
  return returnDecimalFormatting(numPrice, notation, true) || "";
}

/**
 * Format with notation for display
 * Applies notation-based rounding AND includes notation suffix.
 * When price is null/empty, returns notation as placeholder.
 * For $fr: displays the fraction string with " $fr" suffix.
 *
 * @param price - The fill price value (string from API)
 * @param notation - The notation type for determining decimal places and suffix
 * @returns Formatted string for display with notation suffix, or notation as placeholder
 */
export function formatFillPriceWithNotation(
  price: string | undefined,
  notation?: NotationToDecimalValueType | string,
): string {
  if (price === null || price === undefined || price === "") {
    // Return "price" placeholder for $p/$fr, otherwise notation
    return isPriceNotation(notation) ? "price" : (notation || "");
  }

  // $fr: if the price is a fraction string, format without suffix
  if (notation === "$fr" && containsHyphenInQuote(price)) {
    return formatDollarFractionString(price);
  }

  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    // Return "price" placeholder for $p/$fr, otherwise notation
    return isPriceNotation(notation) ? "price" : (notation || "");
  }

  // Use "$" for decimal place calculation when notation is a resolved currency symbol
  const roundingNotation = (notation && isCurrencyNotation(notation))
    ? "$" as NotationToDecimalValueType
    : notation as NotationToDecimalValueType | undefined;

  // Apply notation-based rounding
  const formattedValue = returnDecimalFormatting(numPrice, roundingNotation, true);
  if (!formattedValue) {
    // Return "price" placeholder for $p/$fr, otherwise notation
    return isPriceNotation(notation) ? "price" : (notation || "");
  }

  return formatValueWithNotation(formattedValue, notation);
}

/**
 * AG Grid valueFormatter for copy/paste functionality.
 * Accesses Zustand store directly for current values.
 * Returns value with notation rounding but WITHOUT notation suffix.
 *
 * @param params - AG Grid cell params
 * @returns Formatted string for copy/paste (value only, no notation)
 */
export function fillPriceValueFormatter(params: any): string {
  if (!params.data) {
    return "";
  }

  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);
  const esid = fullTradeData?.esid;

  if (!esid) {
    // Fallback to params.value if we can't get trade data
    return params.value !== null && params.value !== undefined
      ? `${params.value}`
      : "";
  }

  const fillId = params.data.fillId;
  const blockId = params.data.blockId;

  // Get fresh value from Zustand store
  const price = getFillPriceFromStore(esid, fillId, blockId);

  // Get notation for rounding (but don't include suffix in copy value)
  const notation = getNotationFromStore(esid, blockId);

  // If we couldn't get from store, fallback to params.value
  if (price === null || price === undefined) {
    return params.value !== null && params.value !== undefined
      ? `${params.value}`
      : "";
  }

  return formatFillPriceValue(price, notation);
}
